
import React, { useMemo, useEffect, useState } from "react";
import { useStudents } from "@/contexts";
import StatsSection from "@/components/dashboard/StatsSection";
import ChartsSection from "@/components/dashboard/ChartsSection";
import SidebarSection from "@/components/dashboard/SidebarSection";
import TeachersTable from "@/components/dashboard/TeachersTable";
import AdminsOverviewCard from "@/components/dashboard/AdminsOverviewCard";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { getTeachers } from "@/services/db/teachers";
import { getAdmins } from "@/services/db/admins";
import { toast } from "sonner";
import { useTeacherNotifications } from "@/hooks/useTeacherNotifications";
import { useNavigate } from "react-router-dom";
import { Admin } from "@/types/admin.types";
import { Teacher } from "@/types/teacher.types";

const Dashboard = () => {
  const navigate = useNavigate();
  const { students, loading } = useStudents();
  const [teachers, setTeachers] = useState<Partial<Teacher>[]>([]);
  const [admins, setAdmins] = useState<Partial<Admin>[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  
  const { checkNotifications } = useTeacherNotifications();

  const { stats, programData, enrollmentData, loading: loadingStats, forceRefresh } = useDashboardStats();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingTeachers(true);
        setLoadingAdmins(true);
        
        const teachersData = await getTeachers();
        setTeachers(teachersData);
        
        const adminsData = await getAdmins();
        setAdmins(adminsData);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoadingTeachers(false);
        setLoadingAdmins(false);
      }
    };

    fetchData();
  }, [refreshKey]);

  const enhancedStats = useMemo(() => {
    if (!stats) return null;

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfYear = new Date(now.getFullYear(), 0, 1);

    let pendingAmount = 0;
    let partialAmount = 0;

    students.forEach(student => {
      student.programs.forEach(program => {
        if (program.feeStatus === 'pending') {
          pendingAmount += (program.fee || 0);
        } else if (program.feeStatus === 'partial') {
          partialAmount += (program.fee || 0) / 2;
        }
      });
    });

    const monthlyRevenue = students.reduce((acc, student) => {
      return acc + student.programs.reduce((sum, program) => {
        const enrollDate = new Date(program.enrolledAt);
        return enrollDate >= startOfMonth ? sum + (program.fee || 0) : sum;
      }, 0);
    }, 0);

    const annualRevenue = students.reduce((acc, student) => {
      return acc + student.programs.reduce((sum, program) => {
        const enrollDate = new Date(program.enrolledAt);
        return enrollDate >= startOfYear ? sum + (program.fee || 0) : sum;
      }, 0);
    }, 0);

    return {
      totalStudents: stats.totalStudents,
      totalPrograms: stats.totalPrograms,
      totalRevenue: stats.totalFees,
      newStudents: stats.recentEnrollments,
      totalTeachers: teachers.length || 0,
      revenuePeriods: {
        monthly: monthlyRevenue,
        annual: annualRevenue
      },
      pendingAmount,
      partialAmount
    };
  }, [stats, teachers.length, students]);

  if (!enhancedStats) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold tracking-tight text-nalc-dark dark:text-white">Dashboard</h2>
      </div>
      
      <StatsSection stats={enhancedStats} loading={loadingStats || loadingTeachers} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <ChartsSection 
            enrollmentTrendsData={enrollmentData}
            programDistributionData={programData}
            loading={loadingStats}
          />
          
          <TeachersTable 
            teachers={teachers} 
            loading={loadingTeachers} 
          />
        </div>
        
        <div className="space-y-6">
          <SidebarSection />
          
          <AdminsOverviewCard 
            admins={admins} 
            totalAdmins={admins.length} 
            loading={loadingAdmins} 
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
